<template>
  <div class="pos-ticket-page">
    <div class="pos-ticket-toolbar no-print">
      <button type="button" class="btn-ticket btn-print" @click="printTicket">
        <i class="fas fa-print me-1"></i> Imprimir
      </button>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">
        <i class="fas fa-times me-1"></i> Cerrar
      </button>
    </div>

    <div v-if="loading" class="pos-ticket-empty no-print">
      <p>Cargando ticket...</p>
    </div>

    <div v-else-if="!ticket" class="pos-ticket-empty no-print">
      <p>No hay datos de ticket para mostrar.</p>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">Cerrar</button>
    </div>

    <article v-else class="ticket" id="pos-ticket-print">
      <div v-if="logoUrl" class="ticket-logo-wrap">
        <img :src="logoUrl" alt="Logo empresa" class="ticket-logo" />
      </div>
      <header class="ticket-header">
        <h1 class="ticket-company">{{ companyLine }}</h1>
        <p v-if="companyData.nit" class="ticket-sub">NIT: {{ companyData.nit }}</p>
        <p class="ticket-sub">Tel.: {{ companyData.phone }}</p>
        <p v-if="addressLine" class="ticket-sub">{{ addressLine }}</p>
      </header>

      <section class="ticket-doc">
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div class="ticket-doc-title">Factura de venta</div>
        <div class="ticket-doc-row">
          <span class="ticket-doc-number">No. {{ ticket.invoiceNo || ('FV' + String(ticket.orderId).padStart(8, '0')) }}</span>
          <span class="ticket-doc-date">{{ ticket.dateFormatted || ticket.date }}</span>
        </div>
      </section>

      <section class="ticket-customer">
        <div class="ticket-customer-name">{{ customerName }}</div>
        <div class="ticket-customer-doc">{{ documentLine }}</div>
      </section>

      <section class="ticket-items">
        <div class="ticket-items-head">
          <div class="th-num">#</div>
          <div class="th-qty">Cant.</div>
          <div class="th-desc">
            <span>Descripción</span>
            <span class="th-sub">Impuestos</span>
          </div>
          <div class="th-total">
            <span>Valor total</span>
            <span class="th-sub">Descuento</span>
          </div>
        </div>
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div
          v-for="(item, index) in ticket.items"
          :key="index"
          class="ticket-item-row"
        >
          <div class="td-num">{{ index + 1 }}</div>
          <div class="td-qty">{{ formatQty(item.quantity) }}</div>
          <div class="td-desc">{{ itemLabel(item) }}</div>
          <div class="td-total">{{ money(item.line_total || item.total || (item.unit_price * item.quantity)) }}</div>
        </div>
      </section>

      <section class="ticket-totals">
        <div class="ticket-totals-block">
          <div v-if="ticket.discount > 0 || ticket.discountAmount > 0" class="ticket-total-line">
            <span class="ticket-total-label">DESCUENTO ({{ ticket.discountPercent || 0 }}%) ==></span>
            <span class="ticket-total-value">−{{ money(ticket.discount || ticket.discountAmount) }}</span>
          </div>
          <div v-if="ticket.freight > 0 || ticket.flete > 0" class="ticket-total-line">
            <span class="ticket-total-label">FLETE ==></span>
            <span class="ticket-total-value">+{{ money(ticket.freight || ticket.flete) }}</span>
          </div>
          <div class="ticket-total-line ticket-total-main">
            <span class="ticket-total-label">TOTAL ===========></span>
            <span class="ticket-total-value">{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">RECIBIDO ==></span>
            <span class="ticket-total-value">{{ money(ticket.amountPaid || ticket.received || ticket.total) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">CAMBIO ==></span>
            <span class="ticket-total-value">{{ money(ticket.change || 0) }}</span>
          </div>
        </div>
      </section>

      <section class="ticket-payment">
        <div class="ticket-payment-head">
          <span>Forma de pago</span>
          <span>Valor</span>
        </div>
        <div v-for="(payment, i) in ticket.payments" :key="i" class="ticket-payment-row">
          <span>{{ payment.method }}</span>
          <span>{{ money(payment.amount) }}</span>
        </div>
        <div v-if="!ticket.payments.length" class="ticket-payment-row">
          <span>{{ ticket.paymentMethod }}</span>
          <span>{{ money(ticket.amountPaid || ticket.received || ticket.total) }}</span>
        </div>
      </section>

      <section class="ticket-footer">
        <div class="ticket-seller">VENDEDOR</div>
        <div class="ticket-footer-block">
          <div class="ticket-footer-line">
            <span>TOTAL :</span>
            <span>{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ARTICULOS VEND. :</span>
            <span>{{ ticket.articlesCount || ticket.items.reduce((sum, i) => sum + (i.quantity || 0), 0) }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ITEMS FACT. :</span>
            <span>{{ ticket.itemsCount || ticket.items.length }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>USUARIO :</span>
            <span>{{ ticket.seller || 'POS' }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>FECHA VENCIMIENTO :</span>
            <span>{{ ticket.dateFormatted || ticket.date }}</span>
          </div>
        </div>
      </section>

      <footer class="ticket-legal">
        <div>FRM-POS-001</div>
        <div>Impreso desde MiStock</div>
      </footer>
    </article>
  </div>
</template>

<script>
import settingsService from "@/services/settings.service";
import shopifyService from "@/services/shopify.service";
import shopifyVeService from "@/services/shopifyVenezuela.service";

const body = document.getElementsByTagName("body")[0];

export default {
  name: "ShopifyPosTicket",
  data() {
    return {
      order: null,
      loading: true,
      companySettings: null,
    };
  },
  computed: {
    ticket() {
      if (!this.order) return null;
      const customer = this.order.customer || {};
      const items = Array.isArray(this.order.line_items) ? this.order.line_items : [];
      return {
        orderId: this.order.id,
        invoiceNo: this.order.name,
        date: this.order.created_at
          ? new Date(this.order.created_at).toLocaleString("es-CO", {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })
          : "—",
        clientName: this.order.customer_name || customer.name || "CONSUMIDOR FINAL",
        clientDocument: customer.document || "",
        client: customer,
        items,
        subtotal: Number(this.order.subtotal_price) || 0,
        discountPercent: Number(this.order.discount_percent) || 0,
        discountAmount: Number(this.order.discount_amount) || 0,
        freight: Number(this.order.freight_amount) || 0,
        total: Number(this.order.total_price) || 0,
        amountPaid: Number(this.order.amount_paid) || 0,
        change: Number(this.order.change_amount) || 0,
        paymentMethod: this.order.payment_method || "—",
        payments: Array.isArray(this.order.payments)
          ? this.order.payments.filter((p) => p && p.method && Number(p.amount) > 0)
          : [],
      };
    },
    companyData() {
      return {
        company_name: this.companySettings?.company_name || "MI STOCK",
        nit: this.companySettings?.nit || "",
        phone: this.companySettings?.phone || "—",
        address: this.companySettings?.address || "",
        logo_path: this.companySettings?.logo_path || null,
        logo_path_dark: this.companySettings?.logo_path_dark || null,
      };
    },
    companyLine() {
      return String(this.companyData.company_name).toUpperCase();
    },
    customerName() {
      return String(this.ticket?.clientName || "CONSUMIDOR FINAL").toUpperCase();
    },
    documentLine() {
      const client = this.ticket?.client || {};
      const type = client.document_type || "CC";
      const doc = client.document || "—";
      return `${type} No. : ${doc}`;
    },
    logoUrl() {
      const path = this.companyData.logo_path_dark || this.companyData.logo_path;
      if (!path) return null;
      if (/^https?:\/\//i.test(path)) return path;
      const base = process.env.VUE_APP_API_IMAGE_URL || process.env.VUE_APP_API_BASE_URL || "";
      return `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    },
    addressLine() {
      return this.companyData.address;
    },
  },
  methods: {
    money(value) {
      const n = Number(value) || 0;
      return `$ ${n.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatQty(qty) {
      const n = Number(qty) || 0;
      return Number.isInteger(n) ? String(n) : n.toLocaleString("es-CO", { maximumFractionDigits: 2 });
    },
    itemLabel(item) {
      const name = String(item.name || item.title || "").toUpperCase();
      const suffix = item.ref || item.sku ? ` (${item.ref || item.sku})` : " (Und)";
      return name + suffix;
    },
    printTicket() {
      window.print();
    },
    closeTicket() {
      window.close();
      if (!window.closed) {
        this.$router.push('/shopify/pos');
      }
    },
    async fetchSettings() {
      try {
        const response = await settingsService.get();
        this.companySettings = response?.data || null;
      } catch (e) {
        console.error("Error loading settings:", e);
        this.companySettings = null;
      }
    },
    async fetchOrder() {
      const id = this.$route.query.id;
      if (!id) {
        this.order = null;
        return;
      }
      try {
        const isVe = this.$route.name === 'Shopify Vzla POS Ticket' ||
          String(this.$route.path || '').startsWith('/shopify-ve/');
        const response = isVe
          ? await shopifyVeService.getPosSale(id)
          : await shopifyService.getPosSale(id);
        this.order = response?.data?.order || null;
      } catch (e) {
        console.error("Error loading POS sale:", e);
        this.order = null;
      }
    },
  },
  async mounted() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
    body.classList.remove("g-sidenav-show");

    await Promise.all([this.fetchSettings(), this.fetchOrder()]);
    this.loading = false;
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    body.classList.add("g-sidenav-show");
  },
};
</script>

<style scoped>
.pos-ticket-page {
  min-height: 100vh;
  background: #e9ecef;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pos-ticket-toolbar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 80mm;
  max-width: 100%;
  justify-content: center;
}

.btn-ticket {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-print {
  background: #82d616;
}

.btn-close-tab {
  background: #67748e;
}

.pos-ticket-empty {
  text-align: center;
  padding: 2rem;
}

.ticket {
  width: 80mm;
  max-width: 100%;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.35;
  padding: 3mm 2mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-logo-wrap {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-logo {
  max-width: 56mm;
  max-height: 20mm;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ticket-header {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-company {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 0.25rem;
  line-height: 1.25;
  text-transform: uppercase;
}

.ticket-sub {
  margin: 0;
  font-size: 14px;
}

.ticket-sep {
  text-align: center;
  letter-spacing: 0;
  margin: 0.35rem 0;
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
}

.ticket-doc {
  margin: 0.25rem 0 0.5rem;
  font-size: 14px;
}

.ticket-doc-title {
  margin-bottom: 0.15rem;
}

.ticket-doc-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.ticket-doc-number {
  flex: 1;
  min-width: 0;
}

.ticket-doc-date {
  white-space: nowrap;
  flex-shrink: 0;
}

.ticket-customer {
  margin-bottom: 0.5rem;
  font-size: 14px;
  line-height: 1.35;
}

.ticket-customer-name {
  font-weight: 700;
  text-transform: uppercase;
}

.ticket-customer-doc {
  margin-top: 0.1rem;
}

.ticket-items {
  margin-bottom: 0.35rem;
}

.ticket-items-head,
.ticket-item-row {
  display: grid;
  grid-template-columns: 8% 12% 1fr 34%;
  column-gap: 2px;
  align-items: start;
  font-size: 14px;
}

.ticket-items-head {
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.ticket-item-row {
  margin-bottom: 0.45rem;
  font-size: 14px;
}

.th-num,
.th-qty,
.td-num,
.td-qty {
  text-align: center;
  padding-top: 1px;
}

.th-desc,
.th-total {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.th-desc {
  align-items: center;
  text-align: center;
}

.th-total {
  align-items: flex-end;
  text-align: right;
}

.th-sub {
  font-weight: 400;
  font-size: 12px;
  margin-top: 1px;
}

.td-desc {
  word-break: break-word;
  overflow-wrap: anywhere;
  padding-right: 4px;
  line-height: 1.25;
  text-transform: uppercase;
}

.td-total {
  text-align: right;
  white-space: nowrap;
  padding-top: 1px;
  font-size: 14px;
}

.ticket-totals {
  margin: 0.6rem 0 0.5rem;
  font-size: 14px;
  display: flex;
  justify-content: flex-end;
}

.ticket-totals-block {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.ticket-total-line {
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.2rem 0.35rem;
  justify-content: end;
  margin-bottom: 0.12rem;
  width: fit-content;
  max-width: 100%;
}

.ticket-total-label {
  text-align: right;
  white-space: nowrap;
}

.ticket-total-value {
  text-align: right;
  white-space: nowrap;
  min-width: 4.5rem;
}

.ticket-total-main {
  font-weight: 700;
}

.ticket-total-main .ticket-total-label,
.ticket-total-main .ticket-total-value {
  font-weight: 700;
}

.ticket-payment {
  margin: 0.5rem 0;
  font-size: 14px;
}

.ticket-payment-head,
.ticket-payment-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.ticket-payment-head {
  font-weight: 700;
}

.ticket-footer {
  margin-top: 0.75rem;
  font-size: 14px;
}

.ticket-seller {
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.ticket-footer-block {
  text-align: right;
}

.ticket-footer-line {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  margin-bottom: 0.1rem;
}

.ticket-legal {
  margin-top: 1rem;
  font-size: 14px;
  line-height: 1.35;
  color: #333;
}

@media print {
  @page {
    size: 80mm auto;
    margin: 2mm;
  }

  .no-print {
    display: none !important;
  }

  .pos-ticket-page {
    background: #fff;
    padding: 0;
    min-height: auto;
  }

  .ticket {
    box-shadow: none;
    width: 80mm;
    max-width: 80mm;
    margin: 0 auto;
    font-size: 14px;
  }
}
</style>
